import Modal from '@components/v2/ui/Modal/Modal';
import { ModalFooterConfig } from '@components/v2/ui/Modal/modal.types';
import { Copy } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import useMockLoading from '../../components/Hooks/useMockLoading';
import config from '../../config';
import LangEnum from '../../lang/LangEnum';
import snackbarStore from '../../stores/Snackbar.store';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { StatusType } from '../orders/order.types';
import { useTranslation } from '../translation/TranslationContext';
import SharedEntitiesModal from './SharedEntitiesModal';
import ShareOrderContent from './components/ShareOrderContent';
import useSharedEntities from './hooks/useSharedEntities';
import { createLink, deleteLinks } from './repository/shared-entities.repository';
import { AccessConfig, EntityType, ShareChannelEnum } from './types/sharedEntities.enum';
import {
  IDeleteSharedEntityDto,
  ISharedEntityDto,
  ISharedEntityForm,
  ShareOrderModalProps,
} from './types/sharedEntities.types';

const SharedOrderModalContent = ({
  isOpen,
  onClose,
  orderId,
  status,
  orderCode,
}: ShareOrderModalProps) => {
  const { translate } = useTranslation();
  const { handleShareLinkByWhatsapp, handleShareLinkByEmail, handleCopyLink, canEditOrderAccess } =
    useSharedEntities({});
  const [openDeleteLinksModal, setOpenDeleteLinksModal] = useState(false);
  const { state, team } = useApplicationContext();
  const brand = state?.brand || team?.brand;
  const suggestedOrderPath =
    status === StatusType.SUGGESTED ? `/orders/${orderId}/suggestion` : `/orders/${orderId}`;
  const suggestedOrderURL = `${config.webUrl}/${brand?.slug}?redirectPath=${suggestedOrderPath}`;

  const handleOpenDeleteLinksModal = () => {
    setOpenDeleteLinksModal(true);
  };
  const handleCloseDeleteLinksModal = () => {
    setOpenDeleteLinksModal(false);
  };

  const shareOrderForm = useForm<ISharedEntityForm>({
    defaultValues: {
      accessConfig: AccessConfig.Private,
      config: {
        channelType: ShareChannelEnum.WHATSAPP,
        emails: [],
        catalog: undefined,
      },
    },
  });

  const handlePrivateShareOrder = useCallback(
    async (values: ISharedEntityForm, emails: string[]) => {
      if (values.config.onlyCopy) {
        handleCopyLink(suggestedOrderURL);
        shareOrderForm.setValue('config.onlyCopy', false);
        return;
      }

      if (values.config.channelType === ShareChannelEnum.WHATSAPP) {
        const whatsappMessage = `${translate(
          LangEnum.SUGGESTED_ORDER_WHATSAPP_SHARE_MESSAGE,
        )} \n ${suggestedOrderURL}`;
        handleShareLinkByWhatsapp({ message: whatsappMessage });
        return;
      }

      const globalMessage = `${translate(
        LangEnum.SUGGESTED_ORDER_WHATSAPP_SHARE_MESSAGE,
      )} \n ${suggestedOrderURL}`;

      const htmlMessage = globalMessage.replaceAll('\n', '<br />');

      const data = {
        emails,
        emailBody: htmlMessage,
        emailSubject: translate(LangEnum.GLOBAL_SUGGESTED_ORDER, {
          brandName: state.brand?.name,
        }),
      };

      handleShareLinkByEmail(data);
    },
    [
      suggestedOrderURL,
      shareOrderForm,
      state.brand?.name,
      handleCopyLink,
      handleShareLinkByEmail,
      handleShareLinkByWhatsapp,
      translate,
    ],
  );

  const handlePublicShareOrder = useCallback(
    async (values: ISharedEntityForm, emails: string[]) => {
      const payload: ISharedEntityDto = {
        entity: EntityType.Order,
        entityId: orderId,
        config: {
          channelType: values.config.channelType,
          emails: emails || [],
          catalogId: values.config.catalog?.id,
          catalogName: values.config.catalog?.name,
          code: orderCode,
          entity: EntityType.Order,
          slug: team?.brand?.slug,
        },
      };
      const response = await createLink(payload);

      if (response.ok) {
        if (values.config.onlyCopy) {
          handleCopyLink(response?.data?.url!);
          shareOrderForm.setValue('config.onlyCopy', false);
          return;
        }

        if (values.config.channelType === ShareChannelEnum.WHATSAPP) {
          const globalMessage = `${translate(LangEnum.SUGGESTED_ORDER_WHATSAPP_SHARE_MESSAGE)} \n ${
            response.data?.url
          }`;

          handleShareLinkByWhatsapp({ message: globalMessage });
          return;
        }
        snackbarStore.handleMessage({
          type: 'success',
          message: translate(LangEnum.CATALOGS_SHARE_MODAL_SEND_EMAILS_SUCCESS),
        });

        return;
      }

      snackbarStore.handleMessage({
        type: 'error',
        message: response?.data?.url,
      });
    },
    [orderId, handleCopyLink, handleShareLinkByWhatsapp, shareOrderForm, translate],
  );

  const handleSubmit = useCallback(
    async (values: ISharedEntityForm) => {
      if (!orderId) {
        return;
      }
      const isPrivate = values.accessConfig === AccessConfig.Private;
      const emails = values.config.emails.map((option) =>
        option.id === 'new' ? option.newEmail || '' : option.email,
      );

      if (isPrivate) {
        await handlePrivateShareOrder(values, emails);
        return;
      }

      await handlePublicShareOrder(values, emails);
    },
    [orderId, handlePrivateShareOrder, handlePublicShareOrder],
  );

  const handleDeleteLinks = async () => {
    if (!orderId) {
      return;
    }
    const payload: IDeleteSharedEntityDto = {
      entity: EntityType.Order,
      entityId: orderId,
    };

    const response = await deleteLinks(payload);
    setOpenDeleteLinksModal(false);
    if (response.ok) {
      snackbarStore.handleMessage({
        type: 'success',
        message: translate(LangEnum.SHARE_ENTITY_DELETE_LINK_SUCCESS),
      });
      return;
    }
    snackbarStore.handleMessage({
      type: 'error',
      message: translate(LangEnum.SHARE_ENTITY_DELETE_LINK_FAILED),
    });
  };

  const { isLoading: orderSubmitLoading, handleAsyncFunction: handleSubmitOrder } = useMockLoading({
    asyncFunction: handleSubmit,
  });

  const submitShareOrder = shareOrderForm.handleSubmit(handleSubmitOrder);
  const accessConfig = useWatch({ control: shareOrderForm.control, name: 'accessConfig' });
  const hasErrors = Object.keys(shareOrderForm.formState.errors).length > 0;

  const handleCopyShareLink = () => {
    shareOrderForm.setValue('config.onlyCopy', true);
    submitShareOrder();
  };

  const footer: ModalFooterConfig = {
    primaryAction: {
      label: LangEnum.GLOBAL_SHARE,
      isDisabled: hasErrors,
      isLoading: orderSubmitLoading,
      onClick: submitShareOrder,
    },
    secondaryAction: {
      label: LangEnum.ORDER_CONTRACT_COPY_LINK,
      startIcon: Copy,
      isDisabled: hasErrors,
      onClick: handleCopyShareLink,
    },
    tertiaryAction:
      accessConfig === AccessConfig.Public
        ? {
            label: LangEnum.SHARE_ENTITY_CANCEL_LINKS,
            onClick: handleOpenDeleteLinksModal,
          }
        : undefined,
  };

  return (
    <>
      <Modal
        isOpen={openDeleteLinksModal}
        size="compact"
        header={{ title: LangEnum.SHARE_ENTITY_CANCEL_LINKS_MODAL_TITLE }}
        onClose={handleCloseDeleteLinksModal}
        footer={{
          primaryAction: { label: LangEnum.GLOBAL_YES, onClick: handleDeleteLinks },
          secondaryAction: {
            label: LangEnum.GLOBAL_NO,
            onClick: handleCloseDeleteLinksModal,
          },
        }}
      >
        {translate(LangEnum.SHARE_ENTITY_CANCEL_LINKS_MODAL_SUBTITLE)}
      </Modal>

      <FormProvider {...shareOrderForm}>
        <SharedEntitiesModal
          isOpen={isOpen}
          onClose={onClose}
          title={translate(LangEnum.GLOBAL_SHARE_ORDER_WITH_CODE, { code: orderCode })}
          footer={footer}
          content={
            <ShareOrderContent
              entityId={orderId}
              disabledConfigAccess={status !== StatusType.SUGGESTED && canEditOrderAccess}
            />
          }
        />
      </FormProvider>
    </>
  );
};

export default SharedOrderModalContent;
